import { createTheme } from "@material-ui/core/styles";
import grey from "@material-ui/core/colors/grey";

import Color from "color";

const blue = "#007cc1";
const green = "#74ae34";
const primary = "#2a7cb5";
const red = "#ca6056";
const secondary = "#707070";
const softRed = "#BC0F0F";
const white = "#fff";
const yellow = "#e5a501";

const defaultFontFamily = ["Muli", "sans-serif"].join(",");
const brandFontFamily = ["Museo Sans", "Muli", "sans-serif"].join(",");
const headerFontFamily = defaultFontFamily;
const headerFontWeight = 400;

const Theme = createTheme({
  palette: {
    default: "#fff",
    links: blue,
    blue: blue,
    green: green,
    yellow: yellow,
    black: "#000000",
    primary: {
      light: "#66abe7",
      main: primary,
      dark: "#005085"
    },
    secondary: {
      light: Color(secondary).lighten(0.95).string(),
      main: secondary,
      dark: Color(secondary).darken(0.5).string()
    },
    danger: {
      main: softRed
    },
    error: {
      light: "#ff8982",
      main: red,
      dark: softRed
    },
    success: {
      light: "#54d6a7",
      main: green,
      dark: "#00744c"
    },
    warning: {
      main: yellow
    },
    catalogColors: {
      black: {
        primary: "#000",
        secondary: "#5e5e5e",
        overlay: "#000"
      },
      blue: {
        primary: "#0088cb",
        secondary: "#02b0d8",
        overlay: "#1b8eca"
      },
      green: {
        primary: "#00b593",
        secondary: "#03eabe",
        overlay: "#31bfa5"
      },
      orange: {
        primary: "#F89D1C",
        secondary: "#F7C908",
        overlay: "#fcae32"
      },
      turquoise: {
        primary: "#58c4c8",
        secondary: "#64eded",
        overlay: "#58c3c1"
      }
    }
  },
  border: (width = 1, style = "solid", color = grey[300]) =>
    `${width}px ${style} ${color}`,
  appIcons: { clientApp: red },
  brandSafety: {
    suitableMost: green,
    suitableSome: yellow,
    unsuitable: red,
    notRated: grey[400],
    zeroTolerance: grey[800]
  },
  status: {
    primary: {
      background: primary,
      color: white,
      border: "none",
      "&:hover": {
        background: Color(primary).fade(0.25).string()
      }
    },
    secondary: {
      background: secondary,
      color: white,
      border: "none",
      "&:hover": {
        background: Color(secondary).fade(0.25).string()
      }
    },
    danger: {
      background: softRed,
      color: white,
      border: "none",
      "&:hover": {
        background: Color(softRed).fade(0.25).string()
      }
    },
    success: {
      background: green,
      color: white,
      border: "none",
      "&:hover": {
        background: Color(green).fade(0.25).string()
      }
    },
    warning: {
      background: yellow,
      color: white,
      border: "none",
      "&:hover": {
        background: Color(yellow).fade(0.25).string()
      }
    },
    primaryDisabled: {
      background: grey[200],
      color: grey[500],
      border: "none",
      "&:hover": {
        background: grey[200]
      }
    },
    dangerDisabled: {
      background: softRed,
      color: white,
      border: "none",
      "&:hover": {
        background: Color(softRed).fade(0.25).string()
      }
    },
    successDisabled: {
      background: green,
      color: white,
      border: "none",
      "&:hover": {
        background: Color(green).fade(0.25).string()
      }
    },
    warningDisabled: {
      background: yellow,
      color: white,
      border: "none",
      "&:hover": {
        background: Color(yellow).fade(0.25).string()
      }
    }
  },
  typography: {
    fontFamily: defaultFontFamily,
    fontWeight: {
      default: 400,
      semiBold: 600,
      bold: 700
    },
    brand: {
      fontFamily: brandFontFamily
    }
  },
  props: {
    MuiButtonBase: {
      disableRipple: true
    }
  },
  overrides: {
    MuiOutlinedInput: {
      inputMarginDense: {
        padding: 8,
        paddingTop: 8,
        paddingBottom: 8
      }
    },
    MuiSvgIcon: {
      colorSecondary: {
        color: secondary
      }
    },
    MuiTabs: {
      root: {
        minHeight: 42
      }
    },
    MuiTab: {
      root: {
        border: `1px solid ${grey[400]}`,
        textTransform: "none",
        minHeight: 42,
        minWidth: "min-content !important",
        "&:not(:first-child)": {
          borderLeft: "none"
        },
        "&:first-child": {
          borderTopLeftRadius: 3,
          borderBottomLeftRadius: 3
        },
        "&:last-child": {
          borderTopRightRadius: 3,
          borderBottomRightRadius: 3,
          borderLeft: "none"
        },
        "&$selected": {
          color: grey[900],
          backgroundColor: grey[200]
        }
      },
      textColorInherit: {
        color: primary,
        opacity: 1
      }
    },
    MuiTypography: {
      root: {
        fontFamily: defaultFontFamily
      },
      body2: {
        fontSize: "0.8125rem"
      },
      h4: {
        fontFamily: headerFontFamily,
        fontWeight: headerFontWeight,
        color: blue
      },
      colorError: {
        color: softRed
      },
      colorTextSecondary: {
        color: secondary
      }
    },
    MuiAppBar: {
      root: {
        backgroundColor: white,
        borderBottom: `1px solid ${[grey[300]]}`,
        boxShadow: " 1px 4px 0,rgba(0 0 0 .04)"
      },
      colorDefault: {
        backgroundColor: white
      }
    },
    MuiButton: {
      root: {
        color: primary,
        borderRadius: 0,
        border: `1px solid ${grey[300]}`,
        padding: `8px 16px`,
        textTransform: "none",
        fontSize: "1rem",
        minWidth: 24
      },
      contained: {
        boxShadow: "none",
        "&:active": {
          boxShadow: "none"
        }
      },
      textPrimary: {
        color: white
      },
      textSecondary: {
        color: secondary
      },
      sizeSmall: {
        borderRadius: 3,
        padding: "2px 10px",
        fontSize: "0.75rem",
        minHeight: 24
      },
      outlinedPrimary: {
        border: `1px solid ${primary}`,
        borderRadius: 4
      }
    },
    MuiCard: {
      root: {
        borderRadius: 0
      }
    },
    MuiCardContent: {
      root: {
        "&:last-child": {
          paddingBottom: 16
        }
      }
    },
    MuiDialog: {
      paper: {
        minWidth: "33%",
        maxWidth: "75%",
        position: "relative"
      }
    },
    MuiDialogContent: {
      root: {
        padding: 24
      }
    },
    MuiTableHead: {
      root: {
        background: grey[300],
        whiteSpace: "nowrap"
      }
    },
    MuiTableRow: {
      head: {
        height: 48
      }
    },
    MuiTableCell: {
      root: {
        padding: "16px 12px",
        fontSize: "0.875rem",
        "&:last-child": {
          paddingRight: 12
        }
      },
      sizeSmall: {
        padding: "6px 16px 6px 12px"
      },
      head: {
        fontSize: "13px",
        fontWeight: 700,
        color: grey[600]
      }
    },
    MuiTableSortLabel: {
      root: {
        fontSize: "12px",
        fontWeight: 700,
        color: grey[600]
      },
      active: {
        color: primary,
        "&:hover": {
          color: primary
        },
        "&:focus": {
          color: primary
        }
      }
    },
    MuiMenu: {
      paper: {
        paddingTop: 0,
        paddingBottom: 0,
        borderRadius: 0,
        border: `1px solid ${grey[300]}`,
        boxShadow: "none"
      }
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0
      }
    },
    MuiListItem: {
      button: {
        "&:hover, &:focus": {
          backgroundColor: grey[100]
        }
      }
    },
    MuiListItemText: {
      inset: {
        paddingLeft: "16px",
        paddingRight: "16px"
      }
    },
    MuiListSubheader: {
      root: {
        color: secondary
      }
    },
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: secondary
      }
    },
    MuiPickersDay: {
      day: {
        padding: "0 !important"
      },
      daySelected: { color: "white !important" }
    },
    MuiPickersCalendarHeader: {
      iconButton: {
        "& > *": {
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "transparent"
          }
        }
      }
    },
    MuiAutocomplete: {
      clearIndicator: { padding: "2px !important" },
      popupIndicator: { padding: "2px !important" }
    }
  }
});

export default Theme;
